/**
 * Display / parse helpers for CRM integration field dates (unified Apideck + native SOQL).
 * Keep `INTEGRATION_FIELD_DATE_FORMAT` in sync with `opportunity.closeDate` in template-attributes.
 */
import { format, isMatch, isSameDay, isValid, parse, parseISO } from 'date-fns';

import { DateFormat } from '../time';

export const INTEGRATION_FIELD_DATE_FORMAT = 'MMM d, yyyy';

/**
 * Parse CRM / document date strings without format sniffing via regex.
 * Tries ISO date-only, integration display format, then full ISO datetime.
 */
export function parseIntegrationFieldDate(value: string): Date | null {
  if (!value) return null;

  const dateOnlyPrefix = value.slice(0, DateFormat.DATEONLY_KEBAB.length);
  if (isMatch(dateOnlyPrefix, DateFormat.DATEONLY_KEBAB)) {
    const iso = parseISO(dateOnlyPrefix);
    return isValid(iso) ? iso : null;
  }

  const fromDisplay = parse(value, INTEGRATION_FIELD_DATE_FORMAT, new Date());
  if (isValid(fromDisplay)) return fromDisplay;

  const fromIso = parseISO(value);
  return isValid(fromIso) ? fromIso : null;
}

/**
 * Whether two CRM/document date strings refer to the same calendar day.
 * Used so ISO vs display-format values are not falsely flagged out of sync.
 */
export function integrationFieldDatesAreSameDay(a: string, b: string): boolean {
  const dateA = parseIntegrationFieldDate(a);
  const dateB = parseIntegrationFieldDate(b);
  return Boolean(dateA && dateB && isSameDay(dateA, dateB));
}

/**
 * Format a SOQL/ISO date cell like unified Apideck integration field dates.
 */
export function formatIntegrationFieldDate(raw: string): string | null {
  const date = parseIntegrationFieldDate(raw);
  if (!date) return null;
  return format(date, INTEGRATION_FIELD_DATE_FORMAT);
}
